// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// The opponent.
//
// ── WHY THIS IS NOT A TREE SEARCH ───────────────────────────────────────────────────────────────
// Every other bot in this fleet searches a move tree, because in every other game somebody moves and
// then somebody else moves. Here both sides write the same five seconds at the same instant, so
// there is no "and then": there is no tree, and alpha-beta has nothing to prune. What there is
// instead is a one-shot MATRIX GAME — my plans down the side, their plans across the top, a payoff
// in every cell — and the correct object to compute is not a best move but a MIXED STRATEGY over
// several.
//
// That distinction is the whole reason this opponent is worth playing. A bot that always answers the
// same position the same way is a puzzle: read it once and it is solved for ever, and in a game
// whose entire subject is guessing what the other side guessed, a deterministic opponent has no
// subject. So this one solves the matrix by fictitious play and then SAMPLES from the result. It
// bluffs — not because anybody wrote a bluff, but because the equilibrium of a game like this one is
// genuinely mixed and it is playing the equilibrium.
//
// ── THE FOUR STEPS ──────────────────────────────────────────────────────────────────────────────
//   1. ANCHORS.  Per unit, a handful of destinations worth considering — where it stands now, tiles
//                in and around the mast, and tiles tucked against a corner — each paired with a few
//                directions worth watching and a stance. Never a sample over all 192 tiles: nearly
//                all of them are somewhere nobody would ever go.
//   2. PLANS.    Combinations of those, capped, so the matrix stays small.
//   3. PAYOFF.   Every (mine, theirs) pair run through THE SHIPPED RESOLVER — never an approximate
//                model of it, which is the mistake that lets a bot drift away from the game it is
//                supposedly playing while every test stays green.
//   4. SOLVE.    Fictitious play on the matrix, then sample the resulting mixture.
//
// ── THE BUDGET IS A NODE COUNT, NEVER A MILLISECOND ─────────────────────────────────────────────
// A search that stops when the clock runs out returns a different plan on a slow phone than on a
// fast one. For a solo opponent that is merely unfair; for the balance sim it makes every
// measurement unreproducible, and this fleet has learned that a measurement you cannot reproduce is
// a measurement that will eventually tell you something untrue. So the tiers below are counts, and
// the wall-clock is dealt with by WHEN the bot is asked rather than by how long it may take: the
// outcome of a turn is known the instant both orders exist, so the five seconds the player spends
// watching the replay is five seconds the opponent is not using for anything else.

import { makeRng, type Rng } from '@ben-gy/game-engine/rng';
import {
  N,
  blocksFeet,
  canSeeTile,
  distanceField,
  idx,
  inAnyMastField,
  mastsFor,
  mastField,
  openTiles,
  tx,
  ty,
  type GameMap,
} from './map';
import { MOVE, RUSH, STALK, holdOrder, walkClear, type SideOrders, type Stance, type UnitOrder } from './orders';
import { aliveCount, cloneState, resolve, sectorOf, type MatchState, type Side } from './rules';
import { STANCES } from './tuning';

export type TierId = 'rookie' | 'regular' | 'sharp';

export interface Tier {
  id: TierId;
  name: string;
  blurb: string;
  /** Team plans generated for itself. */
  plans: number;
  /** Team plans it imagines for the opponent. */
  theirs: number;
  /** Fictitious-play iterations. */
  iters: number;
  /** 0 plays the best single answer to the likeliest enemy plan; 1 plays the full mixture. */
  mix: number;
}

export const TIERS: Tier[] = [
  {
    id: 'rookie',
    name: 'Rookie',
    blurb: 'Walks into sight lines. Beatable in your first match.',
    plans: 6,
    theirs: 2,
    iters: 60,
    mix: 0,
  },
  {
    id: 'regular',
    name: 'Regular',
    blurb: 'Solves the turn properly and mixes its answers.',
    plans: 14,
    theirs: 5,
    iters: 400,
    mix: 1,
  },
  {
    id: 'sharp',
    name: 'Sharp',
    blurb: 'Considers twice as much and rarely gives you the same answer twice.',
    plans: 24,
    theirs: 7,
    iters: 900,
    mix: 1,
  },
];

export const DEFAULT_TIER: TierId = 'regular';

export function tierOf(id: string | undefined | null): Tier {
  return TIERS.find((t) => t.id === id) ?? (TIERS.find((t) => t.id === DEFAULT_TIER) as Tier);
}

// ── candidate anchors ───────────────────────────────────────────────────────────────────────────

/** Walking distance to the NEAREST mast, which in Roving is a genuine choice rather than a lookup. */
function nearestMast(map: GameMap, masts: number[]): Int16Array {
  const fields = masts.map((m) => distanceField(map, m));
  if (fields.length === 1) return fields[0];
  const out = new Int16Array(N).fill(-1);
  for (let i = 0; i < N; i++) {
    for (const f of fields) if (f[i] >= 0 && (out[i] < 0 || f[i] < out[i])) out[i] = f[i];
  }
  return out;
}

/** How far a unit can actually get this turn, in tiles, at a given stance. */
const reach = (speed: number, stance: Stance): number => (speed * STANCES[stance].speed) / 100 * 5;

/**
 * Tiles worth going to, for one unit.
 *
 * Deliberately not a sample over open ground. The tiles that matter in this game are: where you
 * already are (holding is a real order and often the right one), the mast and its approaches, and
 * tiles tucked against a wall corner — because a corner is where a wedge stops being able to see
 * you, which is the only kind of safety the game has.
 */
function anchors(map: GameMap, from: number, budget: number, masts: number[], want: number, rng: Rng): number[] {
  const dist = distanceField(map, from);
  const out: number[] = [from];
  const push = (t: number): void => {
    if (t < 0 || t >= N || out.includes(t)) return;
    if (dist[t] < 0 || dist[t] > budget) return;
    if (!walkClear(map, from, t)) return;
    out.push(t);
  };

  // NEAREST MAST FIRST, and this line is load-bearing. Roving stands two masts at once — a
  // rotational pair, exactly symmetric — and taking their tiles in list order meant every unit on
  // both sides considered the SAME mast first, because the shortlist is capped and the second mast's
  // tiles never survived the cut. The map was symmetric, the rules were symmetric, and the search
  // was not: 63% against 20%, stable across seed families, while random-versus-random sat at 44/53.
  // That is the whole reason the sim runs a policy-free control arm — an outcome this lopsided looks
  // exactly like a rules bug and was not one.
  const near = masts.slice().sort((a, b) => (dist[a] < 0 ? 999 : dist[a]) - (dist[b] < 0 ? 999 : dist[b]));
  for (const mast of near) for (const t of mastField(mast)) push(t);

  // Corners: an open tile with a wall beside it and open ground beyond, which is what a peek is.
  const corners: number[] = [];
  for (const t of openTiles(map)) {
    if (dist[t] < 0 || dist[t] > budget) continue;
    let walls = 0;
    for (const [dx, dy] of [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ]) {
      if (blocksFeet(map, tx(t) + dx, ty(t) + dy)) walls++;
    }
    if (walls >= 1 && walls <= 2) corners.push(t);
  }
  // SAMPLED, not sliced. Sorting by distance and taking a prefix looks equivalent and is not: ties
  // in the sort fall back to insertion order, insertion order is row-major tile index, and row-major
  // order is not preserved by rotating the board. So the shortlist a unit got depended on WHICH HALF
  // it was standing in — the boards were mirror-identical, the resolver was mirror-identical (zero
  // violations over sixty seeds), and the search still won 57-43 from one seat, stable across three
  // independent seed families. Drawing the corners with the per-side rng, weighted toward the near
  // ones, removes the systematic part and leaves noise.
  const cap = out.length + want;
  const pool = corners.slice().sort((a, b) => dist[a] - dist[b]);
  while (pool.length && out.length < cap) {
    const i = Math.min(pool.length - 1, Math.floor(rng() * rng() * pool.length));
    push(pool[i]);
    pool.splice(i, 1);
  }

  return out;
}

/** Directions worth watching from a tile: each mast, each enemy, and where you came from. */
function watches(from: number, at: number, masts: number[], foes: Array<{ x: number; y: number }>): number[] {
  const set = new Set<number>();
  const ax = tx(at) + 0.5;
  const ay = ty(at) + 0.5;
  for (const mast of masts) set.add(sectorOf(tx(mast) + 1 - ax, ty(mast) + 1 - ay));
  for (const f of foes) set.add(sectorOf(f.x - ax, f.y - ay));
  if (at !== from) set.add(sectorOf(ax - tx(from) - 0.5, ay - ty(from) - 0.5));
  return [...set];
}

interface Candidate {
  order: UnitOrder;
  /** A cheap static score, used only to bias which combinations get built. */
  score: number;
}

function candidatesFor(
  s: MatchState,
  side: Side,
  n: number,
  masts: number[],
  rng: Rng,
  width: number,
): Candidate[] {
  const map = s.map;
  const unit = s.units.filter((u) => u.side === side)[n];
  if (!unit || !unit.alive) return [{ order: holdOrder(), score: 0 }];
  const from = idx(Math.floor(unit.x), Math.floor(unit.y));
  const foes = s.units.filter((u) => u.side !== side && u.alive);
  const mastDist = nearestMast(map, masts);

  const out: Candidate[] = [];
  for (const stance of [STALK, MOVE, RUSH] as Stance[]) {
    const spots = anchors(map, from, reach(unit.spec.speed, stance), masts, width, rng);
    for (const spot of spots) {
      if (stance === RUSH && spot === from) continue;
      for (const watch of watches(from, spot, masts, foes)) {
        const path = spot === from ? [] : [spot];
        // Cheap heuristics only — the real evaluation is a full resolution of the turn. This exists
        // to decide what is worth resolving, not to decide anything about the game.
        let score = -(mastDist[spot] ?? 20) * 3;
        if (inAnyMastField(masts, tx(spot) + 0.5, ty(spot) + 0.5)) score += 25;
        if (stance === STALK) score += 6;
        if (stance === RUSH) score -= 10;
        for (const f of foes) {
          if (canSeeTile(map, spot, idx(Math.floor(f.x), Math.floor(f.y)))) score -= 4;
        }
        // A tiny jitter, drawn from the per-side stream. Every term above is rotation-invariant, so
        // two mirrored positions produce IDENTICAL scores — and a stable sort then breaks those ties
        // by insertion order, which is derived from tile index and sector index, neither of which
        // survives rotating the board. The shortlist a unit ended up with therefore depended on
        // which half it was standing in. Jitter is smaller than any real score difference, so it
        // only ever decides a tie, which is exactly what it is for.
        out.push({ order: { path, watch, stance }, score: score + rng() * 0.5 });
      }
    }
  }
  out.sort((a, b) => b.score - a.score);
  return out.slice(0, Math.max(3, width * 2));
}

/** Assemble team plans by weighted draws from each unit's shortlist, plus a few pure archetypes. */
function plansFor(s: MatchState, side: Side, masts: number[], rng: Rng, count: number, width: number): SideOrders[] {
  const per: Candidate[][] = [];
  for (let n = 0; n < s.mode.units; n++) per.push(candidatesFor(s, side, n, masts, rng, width));

  const plans: SideOrders[] = [];
  const seen = new Set<string>();
  const add = (plan: SideOrders): void => {
    const key = plan.map((o) => `${o.path.join('.')}|${o.watch}|${o.stance}`).join('/');
    if (seen.has(key)) return;
    seen.add(key);
    plans.push(plan);
  };

  // Everybody holds where they are: the plan a bot that has no ideas should still consider, and
  // often the strongest one on the board.
  add(per.map((c) => ({ ...(c.find((x) => x.order.path.length === 0)?.order ?? c[0].order) })));
  // Everybody's best single idea.
  add(per.map((c) => ({ ...c[0].order })));

  let guard = 0;
  while (plans.length < count && guard++ < count * 12) {
    add(
      per.map((c) => {
        // Biased toward the front of each shortlist without ever ignoring the tail — the tail is
        // where a plan nobody expects comes from.
        const i = Math.min(c.length - 1, Math.floor(rng() * rng() * c.length));
        return { ...c[i].order };
      }),
    );
  }
  return plans;
}

// ── evaluation ──────────────────────────────────────────────────────────────────────────────────

/**
 * What a resolved position is worth to `side`.
 *
 * Bodies dominate, which is correct in a game with one-shot kills — but not so completely that the
 * mast becomes decoration, because a bot that ignores the objective plays a different game from the
 * one the player is playing and is boring in a way no difficulty tier can fix.
 */
function evaluate(after: MatchState, side: Side, masts: number[]): number {
  const me = side;
  const them = (1 - side) as Side;
  if (after.over) {
    if (after.winner === me) return 1e6;
    if (after.winner === them) return -1e6;
  }
  // MY units are worth more than THEIR units are worth removing, and the gap is what stops the bot
  // trading. Weighted equally, a trade is exactly neutral — it costs a body and removes a threat —
  // so the search took every one it was offered, both squads erased each other inside four turns and
  // a third of all matches ended with nobody alive. A human does not play that way, and the reason
  // is not subtle: the unit you keep can still be somewhere later. This asymmetry is the cheapest
  // way to say so, and it changed median match length from 4 turns to something worth watching.
  let v = aliveCount(after, me) * 1000 - aliveCount(after, them) * 760;
  v += (after.hold[me] - after.hold[them]) * 9;
  const dist = nearestMast(after.map, masts);
  for (const u of after.units) {
    if (!u.alive) continue;
    const sign = u.side === me ? 1 : -1;
    const d = dist[idx(Math.floor(u.x), Math.floor(u.y))];
    v += sign * (d >= 0 ? Math.max(0, 18 - d) * 4 : 0);
    if (inAnyMastField(masts, u.x, u.y)) v += sign * 40;
  }
  return v;
}

// ── the matrix ──────────────────────────────────────────────────────────────────────────────────

/**
 * Fictitious play on a zero-sum payoff matrix: each side repeatedly best-responds to the average of
 * what the other has played so far. It converges to the value of the game and leaves behind the
 * frequency with which each row was chosen — which IS the mixed strategy, and is what gets sampled.
 */
function fictitiousPlay(payoff: number[][], iters: number): number[] {
  const rows = payoff.length;
  const cols = payoff[0]?.length ?? 0;
  if (rows === 0 || cols === 0) return [];
  const rowCount = new Array<number>(rows).fill(0);
  const colCount = new Array<number>(cols).fill(0);
  const rowTotals = new Array<number>(rows).fill(0);
  const colTotals = new Array<number>(cols).fill(0);

  for (let it = 0; it < iters; it++) {
    let bestRow = 0;
    for (let r = 1; r < rows; r++) if (rowTotals[r] > rowTotals[bestRow]) bestRow = r;
    rowCount[bestRow]++;
    for (let c = 0; c < cols; c++) colTotals[c] -= payoff[bestRow][c];

    let bestCol = 0;
    for (let c = 1; c < cols; c++) if (colTotals[c] > colTotals[bestCol]) bestCol = c;
    colCount[bestCol]++;
    for (let r = 0; r < rows; r++) rowTotals[r] += payoff[r][bestCol];
  }
  return rowCount;
}

export interface BotChoice {
  orders: SideOrders;
  /** How many full turn resolutions it took — the budget, in the unit the budget is counted in. */
  nodes: number;
}

/**
 * Choose a side's orders for the turn about to be planned.
 *
 * `seed` makes the whole thing reproducible: the same position, tier and seed always produce the
 * same plan, which is what lets the balance sim be a measurement rather than an anecdote — while
 * still being unpredictable to a player, who does not have the seed and is not playing the same
 * position twice.
 */
export function chooseOrders(s: MatchState, side: Side, tier: Tier, seed: number): BotChoice {
  const rng = makeRng((seed ^ (s.turn * 0x9e3779b1) ^ (side * 0x85ebca6b)) >>> 0);
  const masts = mastsFor(s.mode, s.turn);
  const width = Math.max(2, Math.round(tier.plans / 3));

  const mine = plansFor(s, side, masts, rng, tier.plans, width);
  const theirs = plansFor(s, (1 - side) as Side, masts, rng, tier.theirs, Math.max(2, width - 1));

  const payoff: number[][] = [];
  let nodes = 0;
  for (const plan of mine) {
    const row: number[] = [];
    for (const other of theirs) {
      const after = cloneState(s);
      resolve(after, side === 0 ? plan : other, side === 0 ? other : plan, false);
      // resolve() does not score the match — it only runs the window — so ask the position what it
      // is worth rather than what it decided.
      row.push(evaluate(after, side, masts));
      nodes++;
    }
    payoff.push(row);
  }

  const weights = fictitiousPlay(payoff, tier.iters);
  let pick = 0;
  if (tier.mix === 0 || weights.length === 0) {
    // The lowest tier answers the single likeliest plan and never mixes, which is exactly what makes
    // it readable — and being readable is what "Rookie" should mean.
    let best = -Infinity;
    payoff.forEach((row, i) => {
      const worst = Math.min(...row);
      if (worst > best) {
        best = worst;
        pick = i;
      }
    });
  } else {
    const total = weights.reduce((a, b) => a + b, 0);
    let r = rng() * total;
    for (let i = 0; i < weights.length; i++) {
      r -= weights[i];
      if (r <= 0) {
        pick = i;
        break;
      }
    }
  }
  return { orders: mine[pick] ?? mine[0], nodes };
}

/**
 * A control arm for the balance sim: plays the same shape of plan with none of the reasoning.
 *
 * Its whole job is to be BEATEN. A mechanic is only load-bearing if a player blind to it does worse
 * (this fleet has measured a "mechanic" that made no difference at all and shipped it), so the sim
 * asserts a real gap between this and the tiers rather than assuming the search does anything.
 */
export function randomOrders(s: MatchState, side: Side, seed: number): SideOrders {
  const rng = makeRng((seed ^ (s.turn * 2654435761) ^ side) >>> 0);
  const masts = mastsFor(s.mode, s.turn);
  const out: SideOrders = [];
  for (let n = 0; n < s.mode.units; n++) {
    const cands = candidatesFor(s, side, n, masts, rng, 3);
    out.push({ ...cands[Math.floor(rng() * cands.length)].order });
  }
  return out;
}
